/**
 * The Heartwood sound kit — cue catalog and the app-layer play helpers over
 * schmancy's generic `sound` service.
 *
 * Ten functional UI cues, each a WAV master served from web/public/sounds by
 * its exact cue name (the loader maps 1:1 to the file). Cue NAMES live here,
 * domain-agnostic — the sound service only knows "a sample registered under
 * a name"; call sites across the buyer journey fire them by name.
 *
 * `sound.hasSample('error')` is also the name schmancy's own notification
 * channel checks before a `$notify.error(...)` toast dings — registering the
 * "error" cue here routes every such toast through the Heartwood master
 * automatically, no separate wiring needed at those call sites.
 */
import { sound } from "@mhmo91/schmancy/audio";

export const SOUND_CUES = {
	"add-to-cart": "/sounds/add-to-cart.wav",
	"remove-from-cart": "/sounds/remove-from-cart.wav",
	saved: "/sounds/saved.wav",
	"order-sent": "/sounds/order-sent.wav",
	"new-order": "/sounds/new-order.wav",
	"overdue-alert": "/sounds/overdue-alert.wav",
	error: "/sounds/error.wav",
	"form-error": "/sounds/form-error.wav",
	welcome: "/sounds/welcome.wav",
	cancel: "/sounds/cancel.wav",
} as const;

export type SoundCue = keyof typeof SOUND_CUES;

/** Register every cue with the sound service. Cheap — cue→URL Map writes
 *  only, no fetch, no AudioContext — safe to run at boot on the public
 *  event landing. */
export function registerSounds(): void {
	for (const cue of Object.keys(SOUND_CUES) as SoundCue[]) {
		sound.registerSample(cue, SOUND_CUES[cue]);
	}
}

/** Warm the decode cache for a snappy first play. Deferred to the first user
 *  gesture (see boot-audio.ts) so a visitor who never interacts never pays
 *  for ~570 KB of fetched masters or an AudioContext. */
export function preloadSounds(): void {
	for (const cue of Object.keys(SOUND_CUES) as SoundCue[]) {
		sound.preloadSample(cue);
	}
}

/** Play a cue at its action call site. `dropIfSuspended` is for cues fired
 *  outside a direct gesture (an arrival pushed while the tab is idle) —
 *  drop rather than queue a stale chime if audio hasn't been unlocked yet. */
export function playSoundCue(
	cue: SoundCue,
	opts?: { dropIfSuspended?: boolean },
): void {
	// A cue is never the act itself: with no Web Audio (a test DOM, a locked
	// down browser) the act proceeds silently.
	if (!("AudioContext" in globalThis || "webkitAudioContext" in globalThis))
		return;
	sound.playCue(cue, opts);
}

/** Resume audio from within the session's first user gesture. */
export function unlockAudio(): void {
	sound.unlock();
}
